import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Ghost, MapPin, Calendar, Send, ExternalLink, Camera, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';

function Chat({ booking, me }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const load = () => api.get(`/chat/${booking.booking_id}`).then(r => setMsgs(r.data)).catch(()=>{});
  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, [booking.booking_id]);
  const send = async () => { if (!text.trim()) return; await api.post('/chat/send', { booking_id: booking.booking_id, message: text }); setText(''); load(); };
  return (
    <div>
      <div className="bg-[#0a0a0a] rounded-xl p-3 h-48 overflow-y-auto space-y-2 border border-[#3f2e1e]">
        {msgs.length===0 && <div className="text-xs text-[#a3a3a3] italic">No messages yet.</div>}
        {msgs.map(m => (
          <div key={m.message_id} className={`text-sm ${m.sender_id===me.user_id?'text-right':''}`}>
            <div className="text-[10px] text-[#a3a3a3] uppercase font-accent">{m.sender_name || m.sender_role}</div>
            <div className={`inline-block px-3 py-2 rounded-2xl ${m.sender_id===me.user_id?'bg-[#f97316] text-[#0a0a0a]':'bg-[#262626]'}`}>{m.message}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <input className="input-cozy flex-1" value={text} onChange={e=>setText(e.target.value)} placeholder="Reply..." onKeyDown={e=>e.key==='Enter'&&send()}/>
        <button onClick={send} className="btn-primary"><Send size={14}/></button>
      </div>
    </div>
  );
}

function VideoReplyCard() {
  const [recording, setRecording] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setVideoUrl(URL.createObjectURL(blob));
        setVideoReady(true);
        stream.getTracks().forEach((track) => track.stop());
      };
      recorder.start();
      setRecording(true);
      window.setTimeout(() => {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
        setRecording(false);
      }, 90000);
    } catch {
      toast.error('Camera access was blocked or unavailable.');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    setRecording(false);
  };

  return (
    <div className="magical-card p-5 mb-8 border-[#f97316]/50">
      <div className="flex items-start gap-3 mb-4">
        <Camera className="text-[#f97316] shrink-0" size={28}/>
        <div>
          <h2 className="font-heading text-2xl mb-2">Video reply</h2>
          <p className="text-sm text-[#a3a3a3] leading-relaxed">
            To continue with your application, record a quick 90-second reply: tell us your city, one strange local story you love, and what kind of traveller you would be excited to meet.
          </p>
        </div>
      </div>
      <div className="grid lg:grid-cols-[1fr_0.9fr] gap-4 items-start">
        <div>
          <div className="flex flex-wrap gap-3">
            {!recording && <button type="button" onClick={startRecording} className="btn-secondary text-sm">Start recording</button>}
            {recording && <button type="button" onClick={stopRecording} className="btn-primary text-sm">Stop recording</button>}
          </div>
          {videoReady && <p className="mt-4 text-sm text-[#f97316] flex items-center gap-2"><CheckCircle2 size={16}/> Video response completed locally.</p>}
          <p className="text-xs text-[#737373] mt-3">Local browser preview only. A production provider is still needed for secure upload and email delivery; Ghostie should not store the raw recording.</p>
        </div>
        <video ref={videoRef} src={videoUrl} controls autoPlay muted className="w-full rounded-2xl bg-black aspect-video object-cover border border-[#3f2e1e]" />
      </div>
    </div>
  );
}

export default function HostDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);
  const load = async () => {
    const [p, a, m] = await Promise.all([
      api.get('/hosts/me').then(r=>r.data).catch(()=>null),
      api.get('/hosts/available-bookings').then(r=>r.data).catch(()=>[]),
      api.get('/hosts/my-bookings').then(r=>r.data).catch(()=>[]),
    ]);
    setProfile(p); setAvailable(a); setMine(m);
  };
  useEffect(() => { load(); }, []);

  const apply = async (id) => {
    try { await api.post('/hosts/apply-booking', { booking_id: id }); toast.success('Applied! Admin will review.'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <Ghost size={48} className="text-[#f97316] mx-auto mb-3"/>
        <h1 className="text-2xl font-heading font-semibold mb-2">No host profile</h1>
        <p className="text-[#a3a3a3]">Apply via "Become a Ghostie" to get started.</p>
      </div>
    );
  }

  const statusLabels = {
    application_received:'Application received', interview_pending:'Interview pending',
    contract_pending:'Contract pending', identity_check_pending:'Identity check pending',
    approved_active:'Approved · Active', paused:'Paused', suspended:'Suspended', rejected:'Rejected',
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center gap-3 mb-8">
        <Ghost size={28} className="text-[#f97316]"/>
        <h1 className="text-3xl font-heading font-semibold">Hi {user?.name?.split(' ')[0]} — your Ghostie HQ</h1>
      </div>

      <div className="magical-card p-5 mb-8">
        <div className="text-xs uppercase text-[#f97316] font-accent tracking-widest">Status</div>
        <div className="font-heading text-2xl text-[#f97316] mt-1">{statusLabels[profile.status] || profile.status}</div>
        <div className="text-xs text-[#a3a3a3] mt-2">City: {profile.city_id} · Rate: €{profile.payout_rate_per_hour}/hr · 5★ bonus €{profile.five_star_bonus}</div>
      </div>

      {!profile.identity_verified && (
        <div className="magical-card p-5 mb-8 border-[#f97316]/50">
          <h2 className="font-heading text-2xl mb-2">Verify you are 18+</h2>
          <p className="text-sm text-[#a3a3a3] leading-relaxed mb-4">
            To continue with your application, please verify that you are 18+. Your ID will be checked securely by an external provider and won’t be stored by Ghostie.
          </p>
          <button className="btn-secondary text-sm" type="button">Verify 18+</button>
        </div>
      )}

      <VideoReplyCard />

      {profile.status !== 'approved_active' && (
        <div className="magical-card p-6 mb-8 border-[#f97316]/60">
          <h2 className="font-heading text-2xl mb-2">Your profile is not active yet</h2>
          <p className="text-[#a3a3a3] mb-4">
            Il tuo profilo non è stato ancora attivato, per favore prenota una Introductory Call. Le nostre chiamate sono gestite con Google Meet.
          </p>
          <a href="https://cal.com/team/ghostie/introcall" target="_blank" rel="noreferrer" className="btn-primary inline-flex">
            Book Introductory Call <ExternalLink size={14}/>
          </a>
        </div>
      )}

      <div className="magical-card p-5 mb-8">
        <h2 className="font-heading text-2xl mb-3">Profile & payout</h2>
        <p className="text-sm text-[#a3a3a3] mb-4">
          Soon you’ll be able to edit every part of your public Ghostie profile here. For payout setup, pick your country and add IBAN/local bank details after HQ activates you.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <select className="input-cozy" defaultValue="">
            <option value="" disabled>Country for payout...</option>
            <option>Italy</option><option>United Kingdom</option><option>United States</option><option>Czech Republic</option><option>Other</option>
          </select>
          <input className="input-cozy" placeholder="IBAN / local payout details" defaultValue={profile.payout_method_notes || ''}/>
        </div>
      </div>

      {profile.status === 'approved_active' && (
        <>
          <h2 className="font-heading text-2xl mb-4">Available Ghostie Time in your city</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {available.length===0 && <div className="text-[#a3a3a3] text-sm">No open bookings right now.</div>}
            {available.map(b => (
              <div key={b.booking_id} className="magical-card p-5" data-testid={`avail-${b.booking_id}`}>
                <div className="flex items-center gap-2 text-sm text-[#a3a3a3] mb-2">
                  <Calendar size={14}/> {b.date} {b.start_time} · {b.duration_hours}h · {b.number_of_people} ppl
                </div>
                <div className="text-sm text-[#a3a3a3] mb-2"><MapPin size={12} className="inline"/> {b.city_id}</div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {(b.interest_tags||[]).map(t => <span key={t} className="badge-cozy text-[10px]">{t}</span>)}
                </div>
                {b.special_requests && <p className="text-xs text-[#a3a3a3] italic mb-3">"{b.special_requests}"</p>}
                <button onClick={()=>apply(b.booking_id)} className="btn-primary text-sm">Apply for this Ghostie Time</button>
              </div>
            ))}
          </div>
        </>
      )}

      <h2 className="font-heading text-2xl mb-4">My assigned meetups</h2>
      <div className="space-y-4">
        {mine.length===0 && <div className="text-[#a3a3a3] text-sm">Nothing yet.</div>}
        {mine.map(b => (
          <div key={b.booking_id} className="magical-card p-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="font-heading text-lg">{b.date} · {b.start_time}</div>
                <div className="text-xs text-[#a3a3a3]">{b.duration_hours}h · {b.number_of_people} ppl · {b.preferred_language}</div>
                <div className="badge-cozy mt-2 text-[10px]">{b.status.replace(/_/g,' ')}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#a3a3a3]">Customer</div>
                <div className="font-semibold">{b.customer_name}</div>
              </div>
            </div>
            {b.special_requests && <p className="text-sm text-[#a3a3a3] mt-3 italic">"{b.special_requests}"</p>}
            {['confirmed_chat_open','completed','review_requested'].includes(b.status) && user && (
              <div className="mt-4"><Chat booking={b} me={user}/></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
